import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Facebook, Youtube, Mail, Phone } from "lucide-react";
import { LOGO_URL } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/films", label: "Wedding Films" },
  { to: "/services", label: "Services" },
  { to: "/blog", label: "Blog" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/book", label: "Book Call" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", f); return () => window.removeEventListener("scroll", f);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/5" : "bg-gradient-to-b from-black/60 to-transparent"}`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <img
            src={LOGO_URL}
            alt="Elite Pixels — Photography & Videography"
            className={`object-contain transition-all duration-500 ${scrolled ? "h-12 md:h-14" : "h-14 md:h-16"} drop-shadow-[0_2px_10px_rgba(212,175,55,0.15)] group-hover:drop-shadow-[0_2px_14px_rgba(212,175,55,0.35)]`}
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-5">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) =>
                `text-[0.72rem] tracking-[0.16em] uppercase font-medium transition-colors ${isActive ? "text-[#D4AF37]" : "text-white/80 hover:text-[#D4AF37]"}`
              }>{n.label}</NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <Link to="/contact#availability" data-testid="header-cta" className="btn-gold">Check Availability</Link>
        </div>

        <button onClick={() => setOpen(!open)} className="xl:hidden text-white p-2" data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden fixed inset-0 top-[80px] bg-black/95 backdrop-blur-xl border-t border-white/5 overflow-y-auto" data-testid="mobile-menu">
          <div className="flex flex-col p-8 gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to}
                className={({ isActive }) =>
                  `py-4 border-b border-white/5 font-serif text-2xl tracking-wide ${isActive ? "text-[#D4AF37]" : "text-white"}`
                }>{n.label}</NavLink>
            ))}
            <Link to="/contact#availability" className="btn-gold mt-6 w-full" data-testid="mobile-cta">Check Availability</Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer({ settings = {} }) {
  const s = {
    instagram: "https://instagram.com/elitepixels_",
    youtube: "https://youtube.com/@elitepixels330",
    facebook: "",
    tiktok: "",
    email: "Muj.emal@elitepixels.ca",
    phone: "+1 647 640 3625",
    ...settings,
  };
  return (
    <footer className="bg-black border-t border-white/10 mt-24" data-testid="site-footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-20 pb-10">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <img src={LOGO_URL} alt="Elite Pixels" className="h-20 object-contain mb-5 drop-shadow-[0_2px_14px_rgba(212,175,55,0.2)]" />
            <p className="text-white/60 max-w-md leading-relaxed mt-2">
              Wedding photography and films, told honestly. Quietly capturing love stories across
              Canada, the United States, and wherever your story takes you.
            </p>
            <div className="flex gap-3 mt-6">
              {s.instagram && <a href={s.instagram} target="_blank" rel="noreferrer" className="h-10 w-10 border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center transition" data-testid="footer-instagram"><Instagram size={16} /></a>}
              {s.youtube && <a href={s.youtube} target="_blank" rel="noreferrer" className="h-10 w-10 border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center transition" data-testid="footer-youtube"><Youtube size={16} /></a>}
              {s.facebook && <a href={s.facebook} target="_blank" rel="noreferrer" className="h-10 w-10 border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center transition"><Facebook size={16} /></a>}
            </div>
          </div>

          <div>
            <div className="eyebrow mb-4">Explore</div>
            <ul className="space-y-3 text-sm text-white/70">
              {NAV.slice(1, 7).map(n => <li key={n.to}><Link to={n.to} className="hover:text-[#D4AF37] transition">{n.label}</Link></li>)}
            </ul>
          </div>

          <div>
            <div className="eyebrow mb-4">Contact</div>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-center gap-2"><Mail size={14} className="text-[#D4AF37]" /> <a href={`mailto:${s.email}`} className="hover:text-[#D4AF37]">{s.email}</a></li>
              <li className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> <a href={`tel:${s.phone.replace(/\s/g,'')}`} className="hover:text-[#D4AF37]">{s.phone}</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/40">
          <span>© {new Date().getFullYear()} Elite Pixels. All rights reserved.</span>
          <span>Wedding Photography & Films — Told Honestly</span>
        </div>
      </div>
    </footer>
  );
}
